import React, { JSX, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, GridLegacy as Grid } from '@mui/material';

import { HOTEL_OFFER_TYPES } from '~/consts/offerTypes';

import ReservationService from '~/services/ReservationService';

interface Props {
  offer: App.AccommodationOffer;
}

interface Field {
  label: string;
  value: string;
}

function getUniquePropertyIds(packages, idField) {
  return packages
    .map((pkg) => pkg[idField])
    .filter((pkgId, index, ids) => pkgId && typeof pkgId === 'string' && ids.indexOf(pkgId) === index);
}

const getDateChangeMessage = (numberOfChanges: string): string => {
  if (numberOfChanges === null) {
    return 'NO';
  }
  if (numberOfChanges.toLowerCase() === 'unlimited') {
    return 'UNLIMITED';
  }
  if (numberOfChanges === '0') {
    return 'NO';
  }
  return `${numberOfChanges} LEFT`;
};

export default function OfferChecks({ offer }: Props): JSX.Element {
  const [channelManager, setChannelManager] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const hasVideo = useMemo(
    () => Object.values(offer.videos).some((videoGroup) => Object.values(videoGroup).some((video) => !!video)),
    [offer.videos],
  );
  const hasBadge = useMemo(() => Object.values(offer.badges).some((badge) => !!badge), [offer.badges]);

  useEffect(() => {
    const fetchChannelManager = async () => {
      try {
        const isHotelOffer = HOTEL_OFFER_TYPES.includes(offer.type);
        const uniqueIds = getUniquePropertyIds(offer.packages, isHotelOffer ? 'fk_property_id' : 'fk_tour_id');
        let channelManager = null;

        for (const propertyId of uniqueIds) {
          const propertyResult = await ReservationService.getProperty(propertyId);
          channelManager = propertyResult.result?.channel_manager || null;
          if (channelManager) {
            break;
          }
        }

        return channelManager;
      } catch (e) {
        console.error('Error fetching channel manager:', e);
        enqueueSnackbar(e.message, { variant: 'error' });
      }
    };

    fetchChannelManager().then((channelManager) => {
      setChannelManager(channelManager);
    });
  }, [enqueueSnackbar, offer.packages, offer.type]);

  const fields = useMemo(() => {
    const fields: Array<Field> = [];

    const updateField = (condition: boolean, label: string, positiveMessage: string, negativeMessage: string) => {
      const field = { label, value: condition ? positiveMessage : negativeMessage };
      fields.push(field);
    };

    const updateHasVideoField = (hasVideo: boolean, videos: any) => {
      const descriptions: Array<string> = [];
      if (hasVideo) {
        descriptions.push('YES');
        const { luxuryescapes } = videos;
        if (luxuryescapes.is_video_vimeo_hero) {
          descriptions.push('HERO');
        }
      } else {
        descriptions.push('NO');
      }
      const descriptionMessage = descriptions.join(' - ');
      fields.push({ label: 'HAS VIDEO?', value: descriptionMessage });
    };

    updateHasVideoField(hasVideo, offer.videos);
    updateField(
      getDateChangeMessage(offer.number_of_date_changes) !== 'NO',
      'DATE CHANGE ALLOWED?',
      getDateChangeMessage(offer.number_of_date_changes),
      'NO',
    );

    return fields;
  }, [hasVideo, hasBadge, offer.number_of_date_changes]);

  return (
    <Grid container rowGap={3}>
      {fields.map((field, index) => (
        <Grid key={index} item xs={3}>
          <Box>
            {field.label}
            <br />
            {field.value}
            <br />
          </Box>
        </Grid>
      ))}
      <Grid item xs={3}>
        <div>
          CHANNEL MANAGER
          <br />
          {channelManager ?? 'N/A'}
        </div>
      </Grid>
    </Grid>
  );
}
